import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Heading, Flex } from '@amzn/storm-ui';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import { CustomButtonColor } from '../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import {
  CustomSnackbar,
  SnackbarVariants,
} from '../../../UiComponentsLibrary/CustomSnackbar';
import { CustomConfirmationModal } from '../../../UiComponentsLibrary/CustomConfirmationModal';
import { ErrorDisplay } from '../../../Shared/components/ErrorDisplay/ErrorDisplay';
import CustomSpinner from '../../../UiComponentsLibrary/CustomSpinner';
import ArtEventTable from './components/Table';
import EditArtEventForm from '../EditArtEventForm';
import { URLS } from '../../../Shared/constants/url';
import { MODAL_CONFIGS } from '../../config/modalConfig';
import { useEditArtEvent } from '../../hooks/useEditArtEvent';
import { useAppContext } from '../../../Shared/store/appContext';
import { canUserEdit } from '../../../Shared/utils/helpers';

const EditArtEvent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useAppContext();
  const canEdit = canUserEdit(state.currentUser?.permissions);

  const {
    artEventsData,
    isLoading,
    isError,
    error,
    selectedArtEvent,
    snackbarMessage,
    snackbarIsError,
    handleDataTableAction,
    getModalProps,
    handleFormClose,
    handleFormSuccess,
    handleFormError,
    handleSnackbarClose,
  } = useEditArtEvent();

  if (selectedArtEvent) {
    return (
      <div id="edit-art-event-container">
        <Heading id="edit-art-event-header" size="medium">
          {t('edit-art-event-header')}
        </Heading>
        <EditArtEventForm
          artEvent={selectedArtEvent}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          onError={handleFormError}
          waitlistFeatureEnabled={selectedArtEvent.waitlistFeatureEnabled ?? false}
        />
      </div>
    );
  }

  return (
    <>
      <div id="edit-art-event-container">
        <Heading id="edit-art-event-header" size="medium">
          {t('edit-art-event-header')}
        </Heading>
        <CustomCard
          title={t('art-events-card-title')}
          actions={
            <Flex direction="row" columnGap="small">
              <CustomButton
                id="create-art-event-button"
                backgroundColor={CustomButtonColor.submit}
                text={t('create-art-event-button')}
                handleOnClickCb={() => navigate(URLS.CREATE_ART_EVENT)}
                disabled={!canEdit}
                tooltip={!canEdit ? t('no-edit-permission-tooltip') : undefined}
              />
            </Flex>
          }
        >
          {isError ? (
            <div id="edit-art-event-api-error">
              <ErrorDisplay error={error} />
            </div>
          ) : isLoading ? (
            <div id="edit-art-event-spinner">
              <CustomSpinner />
            </div>
          ) : (
            <ArtEventTable
              artEventsData={artEventsData}
              handleDataTableAction={handleDataTableAction}
            />
          )}
        </CustomCard>
      </div>

      {MODAL_CONFIGS.map((config) => {
        const { isOpen, artEvent, isLoading, onConfirm, onCancel } =
          getModalProps(config.type);
        return (
          <CustomConfirmationModal
            key={config.type}
            isOpen={isOpen}
            title={t(config.titleKey)}
            message={t(config.messageKey, {
              artEventName: artEvent?.artEventID,
            })}
            onConfirmCb={onConfirm}
            onCancelCb={onCancel}
            confirmButtonText={t(config.confirmButtonKey)}
            confirmButtonDisabled={isLoading}
          />
        );
      })}

      <CustomSnackbar
        id="edit-art-event-snackbar"
        variant={snackbarIsError ? SnackbarVariants.ERROR : SnackbarVariants.SUCCESS}
        message={t(snackbarMessage)}
        isVisible={!!snackbarMessage}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default EditArtEvent;
